'use client';

import React, { createContext, useContext, useCallback, useRef } from 'react';

// HIPAA Audit Trail Context
// Provides logging functions for PHI access events throughout the app

export type AuditEventType =
  | 'PHI_VIEW' |'PHI_EDIT' |'PHI_EXPORT' |'PHI_PRINT' |'LOGIN' |'LOGOUT' |'SESSION_TIMEOUT' |'ACCESS_DENIED' |'NOTE_SUBMIT' |'NOTE_EDIT' |'CLIENT_VIEW' |'CLIENT_EDIT' |'REPORT_VIEW';

export interface AuditEvent {
  id: string;
  eventType: AuditEventType;
  timestamp: string;
  userId?: string;
  userName?: string;
  resourceType?: string; // 'client', 'shift_note', 'report', etc.
  resourceId?: string;
  resourceName?: string;
  details?: string;
  ipAddress?: string;
}

interface HIPAAComplianceContextValue {
  logAuditEvent: (event: Omit<AuditEvent, 'id' | 'timestamp'>) => void;
  getSessionLog: () => AuditEvent[];
}

const HIPAAComplianceContext = createContext<HIPAAComplianceContextValue>({
  logAuditEvent: () => {},
  getSessionLog: () => [],
});

export function useHIPAACompliance() {
  return useContext(HIPAAComplianceContext);
}

interface HIPAAComplianceProviderProps {
  children: React.ReactNode;
}

export default function HIPAAComplianceProvider({ children }: HIPAAComplianceProviderProps) {
  const eventCounter = useRef(0);

  const logAuditEvent = useCallback((event: Omit<AuditEvent, 'id' | 'timestamp'>) => {
    eventCounter.current += 1;
    const fullEvent: AuditEvent = {
      ...event,
      id: `evt-${Date.now()}-${eventCounter.current}`,
      timestamp: new Date().toISOString(),
    };

    // Store in sessionStorage for current session audit trail
    try {
      const existing = JSON.parse(sessionStorage.getItem('hipaa_audit_trail') || '[]') as AuditEvent[];
      existing.push(fullEvent);
      // Keep last 500 events in session
      const trimmed = existing.slice(-500);
      sessionStorage.setItem('hipaa_audit_trail', JSON.stringify(trimmed));
    } catch {}

    // BACKEND INTEGRATION: POST /api/audit-log { ...fullEvent }
    // In production, send to secure audit log endpoint
    if (process.env.NODE_ENV === 'development') {
      console.info('[HIPAA Audit]', fullEvent.eventType, fullEvent.details || '', fullEvent.timestamp);
    }
  }, []);

  const getSessionLog = useCallback((): AuditEvent[] => {
    try {
      return JSON.parse(sessionStorage.getItem('hipaa_audit_trail') || '[]');
    } catch {
      return [];
    }
  }, []);

  return (
    <HIPAAComplianceContext.Provider value={{ logAuditEvent, getSessionLog }}>
      {children}
    </HIPAAComplianceContext.Provider>
  );
}
